import { toast } from "react-toastify";
import {
  TABLE_HEAD_PORTFOLIO_LONG,
  TABLE_HEAD_PORTFOLIO_SHORT,
  formatValue,
  formatValueDecimal,
  calculateAndFormat,
  calculateAndFormatNoAbs,
} from "../../../component/Methods";

const BENCHMARKS = [
  { name: "NASDAQ", symbol2: "qqq" },
  { name: "S&P 500", symbol2: "spy" },
  { name: "RUSSELL 2000", symbol2: "iwm" },
  { name: "DOW JONES", symbol2: "dia" },
  { name: "RUSSELL 3000", symbol2: "iyk" },
];

function escapeHtml(value) {
  const str = value === undefined || value === null ? "" : String(value);
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\r?\n/g, " ");
}

function htmlCell(value) {
  const content = escapeHtml(value);
  if (!content) return "<td></td>";
  return `<td style="mso-number-format:'\\@';">${content}</td>`;
}

function htmlRow(cells) {
  return `<tr>${cells.map(htmlCell).join("")}</tr>`;
}

function padHtmlRow(cells, width) {
  const row = [...cells];
  while (row.length < width) row.push("");
  return row.slice(0, width);
}

function mergeTablesSideBySideHtml(left, right, gap = TABLE_GAP_COLUMNS) {
  const leftWidth = left.headerLabels.length;
  const rightWidth = right.headerLabels.length;
  const rows = [];

  rows.push(
    htmlRow([
      ...padHtmlRow([left.title], leftWidth),
      ...Array(gap).fill(""),
      ...padHtmlRow([right.title], rightWidth),
    ])
  );

  rows.push(
    htmlRow([
      ...padHtmlRow(left.headerLabels, leftWidth),
      ...Array(gap).fill(""),
      ...padHtmlRow(right.headerLabels, rightWidth),
    ])
  );

  const maxDataRows = Math.max(left.rows.length, right.rows.length);
  for (let i = 0; i < maxDataRows; i += 1) {
    const leftRow = left.rows[i] ?? Array(leftWidth).fill("");
    const rightRow = right.rows[i] ?? Array(rightWidth).fill("");
    rows.push(
      htmlRow([
        ...padHtmlRow(leftRow, leftWidth),
        ...Array(gap).fill(""),
        ...padHtmlRow(rightRow, rightWidth),
      ])
    );
  }

  return rows.join("");
}

const EXCEL_HTML_PREFIX = `<html xmlns:o="urn:schemas-microsoft-com:office:office"
xmlns:x="urn:schemas-microsoft-com:office:excel"
xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta http-equiv="Content-Type" content="text/html; charset=UTF-8"/>
<!--[if gte mso 9]><xml>
<x:ExcelWorkbook xmlns:x="urn:schemas-microsoft-com:office:excel">
<x:ExcelWorksheets>
<x:ExcelWorksheet>
<x:Name>Portfolio</x:Name>
<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet>
</x:ExcelWorksheets>
</x:ExcelWorkbook>
</xml><![endif]-->
<style>
table { border-collapse: collapse; }
td { font-size: 11pt; white-space: nowrap; padding: 2px 6px; }
</style>
</head>
<body>
<table>`;

const EXCEL_HTML_SUFFIX = `</table></body></html>`;

const TABLE_GAP_COLUMNS = 3;

function getPositionTableExportHeaders(tableHeader) {
  return [...tableHeader.slice(0, 8), ...tableHeader.slice(10)];
}

function mergeTablesSideBySide(left, right, gap = TABLE_GAP_COLUMNS) {
  const leftWidth = left.headerLabels.length;
  const rightWidth = right.headerLabels.length;
  const maxDataRows = Math.max(left.rows.length, right.rows.length);
  const lines = [];
  const joinTsvRow = (cells) => cells.join("\t");

  lines.push(
    joinTsvRow([
      ...padHtmlRow([left.title], leftWidth),
      ...Array(gap).fill(""),
      ...padHtmlRow([right.title], rightWidth),
    ])
  );

  lines.push(
    joinTsvRow([
      ...padHtmlRow(left.headerLabels, leftWidth),
      ...Array(gap).fill(""),
      ...padHtmlRow(right.headerLabels, rightWidth),
    ])
  );

  for (let i = 0; i < maxDataRows; i += 1) {
    const leftRow = left.rows[i] ?? Array(leftWidth).fill("");
    const rightRow = right.rows[i] ?? Array(rightWidth).fill("");
    lines.push(
      joinTsvRow([
        ...padHtmlRow(leftRow, leftWidth),
        ...Array(gap).fill(""),
        ...padHtmlRow(rightRow, rightWidth),
      ])
    );
  }

  return lines;
}

function buildTableData(title, headerLabels, rows) {
  return { title, headerLabels, rows };
}

function formatSummaryAum(value) {
  if (value == null || isNaN(value) || !isFinite(value)) return "";
  if (value > 0) return `${value.toFixed(2)}%`;
  if (value < 0) return `(${(value * -1).toFixed(2)}%)`;
  return "";
}

function formatSummaryPl(value) {
  if (value == null || isNaN(value) || !isFinite(value)) return "";
  if (value > 0) return `${formatValueDecimal(value)}`;
  if (value < 0) return `(${formatValueDecimal(value * -1)})`;
  return "";
}

function formatSummaryPercent(value) {
  if (value == null || isNaN(value) || !isFinite(value)) return "";
  if (value > 0) return `${value.toFixed(2)}%`;
  if (value < 0) return `(${(value * -1).toFixed(2)}%)`;
  return "";
}

function formatSummaryRow(row) {
  return [
    row?.name ?? "",
    formatSummaryAum(row?.totalsAUM),
    formatSummaryPl(row?.totalsPl),
    formatSummaryPercent(row?.totalsPcm),
    formatSummaryPl(row?.totalSignals),
  ];
}

function buildBenchmarkRows(livePrices = []) {
  return BENCHMARKS.map((benchmark) => {
    const matchingData = livePrices?.find(
      (data) => data?.symbol === benchmark.symbol2
    );
    let change = "";
    if (matchingData) {
      const { price, yestPrice } = matchingData;
      if (yestPrice) {
        const chPer = (((price - yestPrice) / yestPrice) * 100).toFixed(2);
        change =
          Number(chPer) >= 0 ? `${chPer}%` : `(${Math.abs(chPer)}%)`;
      }
    }
    return [benchmark.name, benchmark.symbol2, change];
  });
}

// ---------------------------------------------------------------------------
// Shared position number formatters (table UI + Excel export)
// ---------------------------------------------------------------------------

export function formatSignedNumber(value, decimals = 2) {
  if (value === undefined || value === null || value === "") return "";
  const num = Number(value);
  if (isNaN(num)) return "";
  if (num >= 0) return decimals != null ? num.toFixed(decimals) : String(num);
  return `(${Math.abs(num).toFixed(decimals)})`;
}

export function formatSignedPercent(value, decimals = 3) {
  if (value === undefined || value === null || value === "") return "";
  const num = Number(value);
  if (isNaN(num)) return "";
  if (num >= 0) return `${num.toFixed(decimals)}%`;
  return `(${Math.abs(num).toFixed(decimals)}%)`;
}

export function formatPositionPl(value) {
  if (value === undefined || value === null || value === "") return "";
  const num = Number(value);
  if (isNaN(num)) return "";
  if (num >= 0) return Math.round(num).toLocaleString();
  return `(${Math.abs(Math.round(num)).toLocaleString()})`;
}

export function formatPositionSize(row, isLong) {
  const raw = isLong ? row?.long_size : Math.abs(row?.short_size ?? 0);
  const formatted = formatValue(raw);
  if (!formatted) return isLong ? "0.00%" : "(0.00%)";
  return isLong ? `${formatted}%` : `(${parseFloat(formatted).toFixed(2)}%)`;
}

export function formatPositionShares(row, isLong) {
  const count = isLong
    ? row?.long_deltaadjustedsharecount
    : row?.short_deltaadjustedsharecount;
  const num = Number(count);
  if (count == null || count === "" || Number.isNaN(num) || num === 0) {
    return "0";
  }
  const formatted = calculateAndFormat(Math.abs(num));
  if (isLong) return num < 0 ? `(${formatted})` : formatted;
  return `(${formatted})`;
}

export function formatPositionSharesValue(count, isLong) {
  const num = Number(count);
  if (count == null || count === "" || Number.isNaN(num)) return "";
  const formatted = calculateAndFormat(Math.abs(num));
  if (!formatted) return "";
  if (isLong) return num < 0 ? `(${formatted})` : formatted;
  return `(${formatted})`;
}

function formatAvgCostPnL(value) {
  if (value == null) return "";
  return value >= 0
    ? calculateAndFormatNoAbs(value)
    : `(${calculateAndFormatNoAbs(Math.abs(value))})`;
}

function formatCostDisplay(value) {
  if (value == null || value === "") return "";
  const num = Number(value);
  if (isNaN(num)) return "";
  return num.toFixed(2);
}

function formatTdgDisplay(tdg) {
  if (tdg == null || Number(tdg) === 0) return "";
  const formatted = formatPositionPl(tdg);
  return Number(tdg) >= 0 ? `${formatted} ` : formatted;
}

/** Green when favorable for the side (long: positive; short: inverted). */
export function getSideColor(
  theme,
  value,
  isLong,
  { treatZeroAsNeutral = false } = {}
) {
  if (value == null || value === "") return theme.palette.text.primary;
  if (treatZeroAsNeutral && Number(value) === 0) {
    return theme.palette.text.primary;
  }
  const isPositive = Number(value) >= 0;
  const favorGreen = isLong ? isPositive : !isPositive;
  const dark = theme.palette.mode === "dark";
  if (favorGreen) return dark ? "#4ade80" : "#16a34a";
  return dark ? "#f87171" : "#dc2626";
}

/** Formatted fields for long/short position table rows. */
export function getRowDisplayValues(row, isLong) {
  const p = isLong ? "long" : "short";
  const last = formatValue(row?.[`${p}_last`]) || "";
  const current = formatValue(row?.[`${p}_current`]) || "";
  const ch = row?.[`${p}_ch`];
  const pl = row?.[`${p}_pL`];
  const chPct = row?.[`${p}_chpercentage`];
  const tdg = row?.[`${p}_tdg`];
  const ticker = row?.[`${p}_tickerSymbol`];

  return {
    size: formatPositionSize(row, isLong),
    shares: formatPositionShares(row, isLong),
    ticker: ticker ? String(ticker).toUpperCase() : "",
    last,
    current,
    ch: formatSignedNumber(ch),
    chRaw: ch,
    pl: formatPositionPl(pl) || "0",
    plRaw: pl,
    chPct: formatSignedPercent(chPct, 3),
    chPctRaw: chPct,
    cost: formatCostDisplay(row?.[`${p}_cost`]),
    plAvgCost: formatAvgCostPnL(row?.[`${p}_pl_avg_cost`]),
    pctAvgCost: formatSignedPercent(row?.[`${p}_percentage_avg_cost`], 2),
    tdg: formatTdgDisplay(tdg),
    tdgRaw: tdg,
    hasTdg: tdg != null && Number(tdg) !== 0,
    showChg: Boolean(last) && Boolean(current) && ch != null && ch !== "",
    showPl: true,
    showChPct: Boolean(chPct),
  };
}

function formatPositionCell(row, columnId, isLong) {
  switch (columnId) {
    case "long_size":
    case "short_size":
      return formatPositionSize(row, isLong);
    case "long_deltaadjustedsharecount":
    case "short_deltaadjustedsharecount":
      return formatPositionShares(row, isLong);
    case "long_tickerSymbol":
    case "short_tickerSymbol":
      return (
        (isLong ? row?.long_tickerSymbol : row?.short_tickerSymbol)?.toUpperCase() ??
        ""
      );
    case "long_last":
    case "short_last":
      return formatValue(isLong ? row?.long_last : row?.short_last) ?? "";
    case "long_current":
    case "short_current":
      return formatValue(isLong ? row?.long_current : row?.short_current) ?? "";
    case "long_ch":
    case "short_ch":
      return formatSignedNumber(isLong ? row?.long_ch : row?.short_ch);
    case "long_pL":
    case "short_pL":
      return formatPositionPl(isLong ? row?.long_pL : row?.short_pL);
    case "long_chpercentage":
    case "short_chpercentage":
      return formatSignedPercent(
        isLong ? row?.long_chpercentage : row?.short_chpercentage,
        2
      );
    case "long_pl_avg_cost":
    case "short_pl_avg_cost":
      return formatPositionPl(
        isLong ? row?.long_pl_avg_cost : row?.short_pl_avg_cost
      );
    case "long_percentage_avg_cost":
    case "short_percentage_avg_cost":
      return formatSignedPercent(
        isLong ? row?.long_percentage_avg_cost : row?.short_percentage_avg_cost,
        2
      );
    case "long_cost":
    case "short_cost":
      return isLong ? row?.long_cost ?? "" : row?.short_cost ?? "";
    case "long_tdg":
    case "short_tdg":
      return isLong ? row?.long_tdg ?? "" : row?.short_tdg ?? "";
    default:
      return row?.[columnId] ?? "";
  }
}

function buildPositionTableData(title, tableHeader, rows, isLong) {
  const headerLabels = tableHeader.map((col) => col.label);
  const dataRows = (rows ?? []).map((row) =>
    tableHeader.map((col) => formatPositionCell(row, col.id, isLong))
  );
  return buildTableData(title, headerLabels, dataRows);
}

export function buildPortfolioExportHtml(snapshot = {}) {
  const {
    titleText = "Funds Performance Summary",
    longShortLabels = ["", "%AUM", "P(L)", "%P(L)", "Signals PnL"],
    totals = [],
    livePrices = [],
    fundVal = "ALL",
    StrategeyVal = "",
    StrategeyTypename = "Both",
    longPositions = [],
    shortPositions = [],
  } = snapshot;

  const summaryTable = buildTableData(
    titleText,
    longShortLabels,
    (totals ?? []).map(formatSummaryRow)
  );

  const benchmarkTable = buildTableData(
    "Benchmark Intraday Performance",
    ["Index", "Symbol", "Change %"],
    buildBenchmarkRows(livePrices)
  );

  const longTable = buildPositionTableData(
    `Longs Positions (${longPositions?.length ?? 0})`,
    getPositionTableExportHeaders(TABLE_HEAD_PORTFOLIO_LONG),
    longPositions,
    true
  );

  const shortTable = buildPositionTableData(
    `Shorts Positions (${shortPositions?.length ?? 0})`,
    getPositionTableExportHeaders(TABLE_HEAD_PORTFOLIO_SHORT),
    shortPositions,
    false
  );

  const filters = escapeHtml(
    `Fund: ${fundVal || "ALL"} | Strategy: ${StrategeyVal || ""} | Side: ${StrategeyTypename || "Both"}`
  );

  return `${EXCEL_HTML_PREFIX}
<tr><td colspan="20"><b>Filters</b> ${filters}</td></tr>
<tr><td colspan="20"></td></tr>
${mergeTablesSideBySideHtml(summaryTable, benchmarkTable)}
<tr><td colspan="20"></td></tr>
${mergeTablesSideBySideHtml(longTable, shortTable)}
${EXCEL_HTML_SUFFIX}`;
}

export function buildPortfolioExportTsv(snapshot = {}) {
  const {
    titleText = "Funds Performance Summary",
    longShortLabels = ["", "%AUM", "P(L)", "%P(L)", "Signals PnL"],
    totals = [],
    livePrices = [],
    fundVal = "ALL",
    StrategeyVal = "",
    StrategeyTypename = "Both",
    longPositions = [],
    shortPositions = [],
  } = snapshot;

  const summaryTable = buildTableData(
    titleText,
    longShortLabels,
    (totals ?? []).map(formatSummaryRow)
  );

  const benchmarkTable = buildTableData(
    "Benchmark Intraday Performance",
    ["Index", "Symbol", "Change %"],
    buildBenchmarkRows(livePrices)
  );

  const longTable = buildPositionTableData(
    `Longs Positions (${longPositions?.length ?? 0})`,
    getPositionTableExportHeaders(TABLE_HEAD_PORTFOLIO_LONG),
    longPositions,
    true
  );

  const shortTable = buildPositionTableData(
    `Shorts Positions (${shortPositions?.length ?? 0})`,
    getPositionTableExportHeaders(TABLE_HEAD_PORTFOLIO_SHORT),
    shortPositions,
    false
  );

  const lines = [
    "sep=\t",
    `Filters\tFund: ${fundVal || "ALL"}\tStrategy: ${StrategeyVal || ""}\tSide: ${StrategeyTypename || "Both"}`,
    "",
    ...mergeTablesSideBySide(summaryTable, benchmarkTable),
    "",
    ...mergeTablesSideBySide(longTable, shortTable),
  ];

  return lines.join("\n");
}

export function getPortfolioExportFileName() {
  const date = new Date();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `portfolio_${month}-${day}-${year}.xls`;
}

export function downloadPortfolioExport(snapshot) {
  const html = buildPortfolioExportHtml(snapshot);
  const blob = new Blob(["\uFEFF", html], {
    type: "application/vnd.ms-excel;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = getPortfolioExportFileName();
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  toast.success("Portfolio exported");
}
